import React from "react"

import { Icon } from "@kit/design/icon"
import Stack from "@kit/design/stack"
import { useFeature } from "hooks/use-feature"

import { usePlanComparisonContext } from "./context"

// Free plans that already include Free Recommendations, so listing it as a
// Creator upgrade benefit would be wrong. Mirrors the RECOMMENDATIONS activity
// in Authorization::AccountPolicy, which grants it to newsletter and kit-free
// accounts. The legacy free plans (free-limited-1000, dev-free) don't
// have it, so they keep seeing it as something they'd gain.
const PLAN_IDS_WITH_FREE_RECOMMENDATIONS = ["kit-free", "newsletter-free"]

export const creatorFeatures = showFreeRecommendations => [
  "Unlimited Visual Automations",
  "Unlimited email sequences",
  "Kit MCP",
  ...(showFreeRecommendations ? ["Free Recommendations"] : []),
  "Paid Recommendations",
  "Smart Recommendations",
  "Remove Kit branding",
  "Apps & integrations",
  "RSS campaigns",
  "Polls"
]

export const creatorProFeatures = optimalTimingEnabled => [
  "Subscriber Signals",
  "Engagement analytics",
  "Advanced A/B testing",
  "Facebook custom audiences",
  optimalTimingEnabled
    ? "Optimal Timing when sending broadcasts"
    : "Newsletter referral system",
  "Growth Concierge services",
  "24/7 Priority support"
]

const featureVariants = ({
  plan,
  showFreeRecommendations,
  optimalTimingEnabled
}) => {
  if (!plan) return []

  if (plan.service_level === "creator") {
    return creatorFeatures(showFreeRecommendations)
  }

  if (plan.service_level === "creator_pro") {
    return creatorProFeatures(optimalTimingEnabled)
  }

  return []
}

const lowerTier = ({ plan }) => {
  if (!plan) return ""

  if (plan.service_level === "creator_pro") {
    return "Creator plan"
  }

  if (plan.service_level === "creator") {
    return "the free plan"
  }

  return ""
}

export function useFeatureList(plan) {
  const { customFeatures = {}, currentPlan } = usePlanComparisonContext()
  const showFreeRecommendations = !PLAN_IDS_WITH_FREE_RECOMMENDATIONS.includes(
    currentPlan?.id
  )
  const optimalTiming = useFeature("optimal_timing")
  const defaultFeatures = featureVariants({
    plan,
    showFreeRecommendations,
    optimalTimingEnabled: optimalTiming.enabled
  })

  return customFeatures[plan?.service_level] || defaultFeatures
}

const FeatureList = ({ plan }) => {
  const lowerTierPlan = lowerTier({ plan })
  const features = useFeatureList(plan)

  return (
    <Stack spacing={4} className="mb-4">
      <p className="font-bold">Everything in {lowerTierPlan} plus:</p>

      {features.map((feature, index) => {
        return (
          <p key={index}>
            <Icon name="check-circle" className="mr-2" />
            {feature}
          </p>
        )
      })}
    </Stack>
  )
}

export { FeatureList }
